import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';

export interface TabItem {
    id: string;
    label: string;
    title?: string;
    category?: string;
    categoryCode?: string;

}

@Component({
    selector: 'app-custom-tabs',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatMenuModule],
    templateUrl: './custom-tabs.component.html',
    styleUrls: ['./custom-tabs.component.scss']
})
export class CustomTabsComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() tabs: TabItem[] = [];
    @Input() control?: FormControl;
    @Input() activeTabId: string = '';

    @Output() tabClicked = new EventEmitter<string>();

    @ViewChild('outer', { static: true }) private outerRef?: ElementRef<HTMLElement>;
    @ViewChildren('measureBtn') private measureBtns?: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('moreMeasureBtn') private moreMeasureBtn?: ElementRef<HTMLElement>;

    visibleTabs: TabItem[] = [];
    overflowTabs: TabItem[] = [];

    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly ngZone = inject(NgZone);
    private resizeObserver?: ResizeObserver;
    private rafId: number | null = null;

    onTabClick(tabId: string) {
        this.activeTabId = tabId;
        if (this.control) {
            this.control.setValue(tabId);
        }
        this.tabClicked.emit(tabId);
    }

    ngAfterViewInit(): void {
        this.visibleTabs = [...(this.tabs ?? [])];
        this.overflowTabs = [];

        // Recalculate when container resizes
        this.ngZone.runOutsideAngular(() => {
            const el = this.outerRef?.nativeElement;
            if (!el || typeof ResizeObserver === 'undefined') return;
            this.resizeObserver = new ResizeObserver(() => this.scheduleRecalculate());
            this.resizeObserver.observe(el);
        });

        // Recalculate when measurements change (e.g. labels/data loaded)
        this.measureBtns?.changes.subscribe(() => this.scheduleRecalculate());

        // Initial
        this.scheduleRecalculate();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabs'] || changes['activeTabId']) {
            this.scheduleRecalculate();
        }
    }

    ngOnDestroy(): void {
        try {
            this.resizeObserver?.disconnect();
        } catch {
            // ignore
        }
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    isOverflowActive(): boolean {
        const active = this.getActiveTabId();
        return !!active && (this.overflowTabs ?? []).some((t) => t.id === active);
    }

    private getActiveTabId(): string {
        const fromControl = (this.control?.value ?? '').toString().trim();
        if (fromControl) return fromControl;
        return (this.activeTabId ?? '').toString().trim();
    }

    private scheduleRecalculate(): void {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
        }
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.ngZone.run(() => this.recalculate());
        });
    }

    private recalculate(): void {
        const tabs = (this.tabs ?? []).filter(Boolean);
        if (!tabs.length) {
            this.visibleTabs = [];
            this.overflowTabs = [];
            return;
        }

        const outerWidth = this.outerRef?.nativeElement?.getBoundingClientRect().width ?? 0;
        const widths = (this.measureBtns?.toArray() ?? []).map((r) => r?.nativeElement?.offsetWidth ?? 0);
        const moreWidth = this.moreMeasureBtn?.nativeElement?.offsetWidth ?? 0;

        if (!outerWidth || widths.length !== tabs.length) {
            // If we can't measure reliably yet, show all.
            this.visibleTabs = [...tabs];
            this.overflowTabs = [];
            this.cdRef.markForCheck();
            return;
        }

        const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
        const totalWidth = sum(widths);

        // All fit
        if (totalWidth <= outerWidth) {
            this.visibleTabs = [...tabs];
            this.overflowTabs = [];
            this.cdRef.markForCheck();
            return;
        }

        const available = Math.max(0, outerWidth - moreWidth);
        let fitCount = 0;
        let running = 0;
        for (let i = 0; i < widths.length; i++) {
            const w = widths[i] ?? 0;
            if (running + w > available) break;
            running += w;
            fitCount++;
        }

        if (fitCount <= 0) fitCount = 1;
        if (fitCount > tabs.length) fitCount = tabs.length;

        let visible = tabs.slice(0, fitCount);
        const activeId = this.getActiveTabId();
        const activeIndex = activeId ? tabs.findIndex((t) => t.id === activeId) : -1;

        // Keep active tab visible by swapping it in.
        if (activeIndex >= 0 && !visible.some((t) => t.id === activeId)) {
            if (visible.length === 0) {
                visible = [tabs[activeIndex]];
            } else {
                visible = [...visible.slice(0, Math.max(0, visible.length - 1)), tabs[activeIndex]];
            }
        }

        const visibleIds = new Set(visible.map((t) => t.id));
        const overflow = tabs.filter((t) => !visibleIds.has(t.id));

        this.visibleTabs = visible;
        this.overflowTabs = overflow;
        this.cdRef.markForCheck();
    }

    isActive(tabId: string): boolean {
        if (this.control) {
            return this.control.value === tabId;
        }
        return this.activeTabId === tabId;
    }
}
